import type {
  GetRolesParams,
  GetRolesResponse,
  RoleHistory,
  RoleHistoryParams,
} from '@kizen/api/role';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type { Drop, WithParams, WithRequired } from './types';

import { getRoleHistory, getRoles } from '@kizen/api/role';
import { getResource } from './resource';
import { curry1 } from './utils';

type RoleHistoryQuery<TData = RoleHistory> = WithRequired<
  QueryObserverOptions<
    RoleHistory,
    DefaultError,
    TData,
    RoleHistory,
    [Resource<Readonly<['role', string, 'history']>, RoleHistoryParams>]
  >,
  'queryFn' | 'queryKey'
>;

type RolesQuery<
  TParams extends GetRolesParams | undefined,
  TData = GetRolesResponse<TParams>,
> = WithRequired<
  QueryObserverOptions<
    GetRolesResponse<TParams>,
    DefaultError,
    TData,
    GetRolesResponse<TParams>,
    [Resource<Readonly<['role']>, TParams>]
  >,
  'queryFn' | 'queryKey'
>;

export const rolesQuery = <
  TParams extends GetRolesParams | undefined,
  TData = GetRolesResponse<TParams>,
>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<RolesQuery<TParams, TData>, 'queryFn' | 'queryKey'>,
    TParams
  > = {}
): RolesQuery<TParams, TData> => {
  const path = ['role'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getRoles(axios, { params: qk.params, signal });
    },
    ...queryOpts,
  };
};

export const roleHistoryQuery = <TData = RoleHistory>(
  axios: AxiosInstance,
  id: string,
  options: WithParams<
    Omit<RoleHistoryQuery<TData>, 'queryFn' | 'queryKey'>,
    RoleHistoryParams
  > = {}
): RoleHistoryQuery<TData> => {
  const path = ['role', id, 'history'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getRoleHistory(axios, qk.path[1], {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

class Queries {
  public roleHistoryQuery: <TData = RoleHistory>(
    ...args: Drop<Parameters<typeof roleHistoryQuery<TData>>>
  ) => RoleHistoryQuery<TData>;
  public rolesQuery: <
    TParams extends GetRolesParams | undefined = undefined,
    TData = GetRolesResponse<TParams>,
  >(
    ...args: Drop<Parameters<typeof rolesQuery<TParams, TData>>>
  ) => RolesQuery<TParams, TData>;

  constructor(axios: AxiosInstance) {
    this.roleHistoryQuery = curry1(roleHistoryQuery)(axios);
    this.rolesQuery = curry1(rolesQuery)(axios);
  }
}

export type RoleQueries = Queries;

export const defRoleQueries = (axios: AxiosInstance): RoleQueries => {
  return new Queries(axios);
};
